
import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  hasNextPage,
  hasPreviousPage
}) => {
  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    const end = Math.min(start + 4, totalPages);

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handlePageClick = (page: number) => {
    if (!isLoading && page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {/* First Page */}
      <button
        className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handlePageClick(1)}
        disabled={isLoading || currentPage === 1 || !hasPreviousPage}
        aria-label="First page"
      >
        <ChevronsLeft className="h-5 w-5" />
      </button>

      {/* Previous Page */}
      <button
        className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handlePageClick(currentPage - 1)}
        disabled={isLoading || currentPage === 1 || !hasPreviousPage}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      {/* Page Numbers */}
      <div className="flex gap-1">
        {getPageNumbers().map(page => (
          <button
            key={page}
            onClick={() => handlePageClick(page)}
            disabled={isLoading}
            className={`
              px-3 py-1 rounded-md transition-colors
              ${currentPage === page
                ? 'bg-brandColor text-white'
                : 'hover:bg-gray-300 dark:hover:bg-gray-800'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            {page}
          </button>
        ))}
      </div>

      {/* Next Page */}
      <button
        className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handlePageClick(currentPage + 1)}
        disabled={isLoading || currentPage === totalPages || !hasNextPage}
        aria-label="Next page"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Last Page */}
      <button
        className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => handlePageClick(totalPages)}
        disabled={isLoading || currentPage === totalPages || !hasNextPage}
        aria-label="Last page"
      >
        <ChevronsRight className="h-5 w-5" />
      </button>
    </div>
  );
};

export default Pagination;